import React, { StrictMode, useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import "bootstrap/dist/css/bootstrap.min.css";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { MsalProvider } from "@azure/msal-react";
import { NotificationProvider } from "./Components/NotificationContext";
import {
  PublicClientApplication,
  EventType,
  BrowserAuthError,
} from "@azure/msal-browser";
import config from "./config/config";
import appInsights from "./config/applicationInsights";
import Loading from "./Components/Loading";

appInsights.loadAppInsights();
appInsights.trackPageView(); // Tracks initial page load
console.log(appInsights);

const currentPage = window.location;
//console.log(currentPage);

const msalConfiguration = {
  auth: {
    clientId: config.clientId, // the only mandatory field in this object, uniquely identifies your app
    // here you'll add the other fields that you might need based on the Azure portal settings
    redirectUri:
      currentPage.pathname.includes("/agent") ||
      currentPage.pathname.includes("/mypurecloud")
        ? config.redirectUri + "agent"
        : config.redirectUri,
    authority: "https://login.microsoftonline.com/" + config.tenantId + "/v2.0",
    postLogoutRedirectUri: config.postLogoutURL,
  },
  cache: {
    cacheLocation: "localStorage",
  },
};

const pca = new PublicClientApplication(msalConfiguration);

/*
const initializeMsal = async () => {
  try {
    await pca.initialize();
 
    await pca
      .handleRedirectPromise()
      .then((tokenResponse) => {
        sessionStorage.setItem("token", tokenResponse.idToken);
      })
      .catch((error) => {
        console.log(error);
        pca.loginPopup();
      });
    const accounts = await pca.getAllAccounts();
 
    console.log(accounts);
 
    if (accounts.length === 0) {
      // User is not authenticated, initiate login redirect
      await pca.loginPopup();
    } else {
      console.log(accounts[0]);
      // Try to acquire a token silently
      const silentTokenResponse = await pca.acquireTokenSilent({
        account: accounts[0],
      });
 
      // Handle the silent token response as needed
      console.log("Silent token response:", silentTokenResponse);
      sessionStorage.setItem("token", silentTokenResponse.idToken);
    }
 
    // Listen for events (optional, for debugging or additional handling)
    pca.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS) {
        console.log("Login successful:", event.payload.account);
      } else if (event.eventType === EventType.LOGIN_FAILURE) {
        console.error("Login failed:", event.error);
      }
    });
  } catch (error) {
    console.error("MSAL initialization error:", error);
  }
};
 
initializeMsal();
*/

const shouldUsePopup = window !== window.parent; // This can be dynamic based on your application's requirements

async function initiateLoginFlow() {
  // Clear session storage to prevent cached credentials
  sessionStorage.clear();

  const loginRequest = {
    scopes: ["openid", "profile", "email"], // Add basic scopes
    prompt: "select_account", // Force account selection
    loginHint: "", // Clear any existing login hint
    account: null, // Ensure no account is pre-selected
  };

  // Function to handle login response
  async function handleLoginResponse(response) {
    if (response && response.account) {
      // Set the new account as active
      await pca.setActiveAccount(response.account);
      appInsights.setAuthenticatedUserContext(response.account.username);

      // Clear any cached location state
      const targetPath = window.location.pathname
        .toLowerCase()
        .includes("/agent")
        ? "/agent"
        : "/";

      // Use replace instead of setting pathname to prevent history issues
      window.location.replace(targetPath);
    } else {
      console.log("No account selected or login failed");
      redirectToUnauthorized();
    }
  }

  // Function to handle login errors
  async function handleLoginError(error) {
    console.error("Login error", error);

    if (
      !currentPage.pathname.includes("/unauthorizedMsal") &&
      !currentPage.pathname.includes("/agent/unauthorizedMsal") &&
      !(error instanceof BrowserAuthError) &&
      !error.errorMessage?.includes("interaction_in_progress")
    ) {
      redirectToUnauthorized();
    }
  }

  try {
    // Clear the token cache before initiating new login
    await pca.clearCache();

    if (shouldUsePopup) {
      console.log("Starting popup login flow");
      await pca
        .loginPopup(loginRequest)
        .then(handleLoginResponse)
        .catch(handleLoginError);
    } else {
      console.log("Starting redirect login flow");
      await pca
        .loginRedirect(loginRequest)
        .then(handleLoginResponse)
        .catch(handleLoginError);
    }
  } catch (error) {
    handleLoginError(error);
  }
}

// Update the initialization function as well
const initializeMsal2 = async () => {
  console.log("Initializing MSAL");
  await pca.initialize();

  try {
    const authResult = await pca.handleRedirectPromise();
    if (authResult) {
      await pca.setActiveAccount(authResult.account);
      return;
    }

    // Check if we need to trigger login
    const accounts = await pca.getAllAccounts();
    if (
      accounts.length === 0 &&
      !window.location.pathname.includes("/postLogout")
    ) {
      await initiateLoginFlow();
    } else if (accounts.length > 0) {
      // Try silent token acquisition
      try {
        const silentTokenResponse = await pca.acquireTokenSilent({
          account: accounts[0],
          scopes: ["openid", "profile", "email"],
        });

        if (silentTokenResponse) {
          await pca.setActiveAccount(accounts[0]);
          appInsights.setAuthenticatedUserContext(accounts[0].username);
        }
      } catch (error) {
        console.error("Silent token acquisition failed:", error);
        await initiateLoginFlow();
      }
    }
  } catch (error) {
    console.error("MSAL initialization error:", error);
    redirectToUnauthorized();
  }
};

function redirectToUnauthorized() {
  if (currentPage.pathname.includes("agent")) {
    window.location = "/agent/unauthorizedMsal";
  } else {
    window.location = "/unauthorizedMsal";
  }
}

//TODO: HANDLE THIS

window.addEventListener("unhandledrejection", function (event) {
  // Log the error to an error reporting service or handle it as needed
  console.error("Uncaught promise rejection:", event.reason, event);

  // log to azure app insights
  appInsights.trackException({
    error: event.reason,
    properties: {
      source: "unhandledrejection",
    },
  });

  //this.window.location = "/errorBoundary";
});

function AppWrapper() {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initializeApp = async () => {
      await initializeMsal2();
      setIsInitialized(true);
    };
    initializeApp();
  }, []);

  if (
    !isInitialized &&
    !window.location.pathname.includes("/postLogout") &&
    !window.location.pathname.includes("/agent/unauthorizedMsal")
  ) {
    return <Loading />; // Loading indicator while initializing
  }

  return (
    <StrictMode>
      <MsalProvider instance={pca}>
        <NotificationProvider>
          <App />
        </NotificationProvider>
      </MsalProvider>
    </StrictMode>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(<AppWrapper />);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

export default pca;
